import { useCallback, useEffect, useState } from 'react';

/** Small data-loading hook: runs `load`, tracks loading/error, exposes reload. */
export function useApiData<T>(
  load: () => Promise<T>,
  deps: unknown[],
  options?: {
    /** How often to poll while `refreshWhile` holds. */
    refreshMs?: number;
    /**
     * Keep polling only while this returns true of the CURRENT data.
     *
     * ── WHY THIS EXISTS ─────────────────────────────────────────────────
     *
     * Some things arrive AFTER the page does. A claim is proposed
     * synchronously and its narrative is drafted on a queue seconds later, so
     * a screen loaded during a sync shows "not drafted yet" and keeps showing
     * it until somebody presses reload. The data was right when it was
     * fetched and stale by the time it was read — which is the shape of bug
     * people report as "the AI didn't run".
     *
     * ── WHY A PREDICATE RATHER THAN A NUMBER ────────────────────────────
     *
     * The condition is a property of the data, so expressing it as a value
     * computed by the caller is circular: `data` would have to exist before
     * the hook that produces it. A predicate is evaluated inside, against
     * whatever the last load returned.
     *
     * It is also self-limiting, which a bare interval is not. Once every
     * claim has a draft this returns false and the polling stops — a table
     * that only changes when somebody clicks something should not cost a
     * request every few seconds per open tab forever.
     */
    refreshWhile?: (data: T | null) => boolean;
  },
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshMs = options?.refreshMs ?? 10_000;
  const refreshWhile = options?.refreshWhile;

  /**
   * Fetch, and RESOLVE WHEN THE FETCH IS DONE.
   *
   * ══════════════════════════════════════════════════════════════════════
   * `await reload()` USED TO BE A LIE
   * ══════════════════════════════════════════════════════════════════════
   *
   * `reload` was the effect's own callback, which returns a CLEANUP FUNCTION.
   * Awaiting a function resolves on the next tick, so nine call sites across
   * the app wrote `await reload()` believing they were waiting for fresh data
   * and were not — the line after ran against the old response.
   *
   * The damage was small and entirely of one kind: a button re-enabled before
   * its table updated, a dialog closed over stale rows. Small, invisible, and
   * wrong in the way that is hardest to notice, because the screen catches up a
   * moment later and looks correct by the time anybody reads it.
   *
   * `takeIf` is how one implementation serves both callers. The effect needs to
   * ABANDON a response when its dependencies change mid-flight; a caller
   * awaiting a refresh needs the promise to resolve either way.
   */
  const fetchInto = useCallback(
    async (takeIf: () => boolean) => {
      if (takeIf()) setLoading(true);
      try {
        const next = await load();
        if (takeIf()) {
          setData(next);
          setError(null);
        }
      } catch (e) {
        if (takeIf()) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (takeIf()) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  /** The effect's callback: starts a fetch, returns the cleanup React expects. */
  const run = useCallback(() => {
    let active = true;
    void fetchInto(() => active);
    return () => {
      active = false;
    };
  }, [fetchInto]);

  /** What CALLERS get. Resolves when the data has actually landed. */
  const reload = useCallback(() => fetchInto(() => true), [fetchInto]);

  useEffect(run, [run]);

  useEffect(() => {
    if (!refreshWhile || !refreshWhile(data)) return;
    let active = true;
    const timer = setInterval(() => {
      /*
        SILENT. The polled refresh never touches `loading`, so the table does
        not flash a spinner every few seconds over data that is already on
        screen and mostly correct. A background refresh that looks like a page
        load is worse than no refresh — it makes a working screen look broken.

        An error here is swallowed for the same reason: the visible data is
        still the last good response, and replacing it with an error banner
        because one poll failed loses more than it reports. A real failure
        surfaces on the next user-initiated load.
      */
      load()
        .then((d) => active && setData(d))
        .catch(() => {});
    }, refreshMs);
    return () => {
      active = false;
      clearInterval(timer);
    };
    /*
      `data` is a dependency, so the predicate is re-evaluated after every
      response and the interval tears down the moment it stops holding.
      Without it the first poll would be the last decision ever made.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshMs, data]);

  return { data, error, loading, reload };
}
